import type { TypedDocumentNode } from '@apollo/client';
import { gql, useMutation } from '@apollo/client';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router';

import { catchAll } from '@sorare/routing';

import { OAuthProvider } from '#src/__generated__/globalTypes.ts';
import { ANY_SPORT_INVITE } from '#src/constants/__generated__/routes.ts';
import { useConfigContext } from '#src/contexts/config/index.ts';
import { useConnectionContext } from '#src/contexts/connection/index.tsx';
import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import { currentUser as currentUserFragment } from '#src/contexts/currentUser/queries.ts';
import { useSnackNotificationContext } from '#src/contexts/snackNotification/index.ts';
import { useSportContext } from '#src/contexts/sport/index.ts';
import { useIsMobileApp } from '#src/hooks/useIsMobileApp.ts';
import { useLocalStorage } from '#src/hooks/useLocalStorage.ts';
import { useOAuthParams } from '#src/hooks/useOAuthParams.ts';
import { useUtmParams } from '#src/hooks/useUtmParams.ts';
import { useEvents } from '#src/lib/events/useEvents.ts';
import { formatGqlErrors } from '#src/lib/gql/index.ts';
import {
  importantNotificationCategories,
  notificationCategories,
} from '#src/lib/notifications.ts';
import { matchAnySportPath } from '#src/lib/routing/generateSportPath.ts';
import { GOOGLE_OAUTH_CLIENT_ID } from '#src/variables.ts';

import type {
  SignInWithOAuthMutation,
  SignInWithOAuthMutationVariables,
} from './__generated__/index.graphql.ts';

const SRC = 'https://accounts.google.com/gsi/client';

declare global {
  interface Window {
    handleOneTapCredential?: (response: any) => void;
  }
}

const SIGN_IN_WITH_OAUTH_MUTATION = gql`
  mutation SignInWithOAuthMutation(
    $input: signInWithOAuthInput!
    $sport: Sport!
    $withCollectionPredictions: Boolean = true
    $notificationCategories: [NotificationCategoryInput!]
    $importantNotificationCategories: [NotificationCategoryInput!]
  ) {
    signInWithOauth(input: $input) {
      currentUser {
        slug
        ...CurrentUserProvider_currentUser
      }
      tcuToken
      errors {
        message
        code
      }
    }
  }
  ${currentUserFragment}
` as TypedDocumentNode<
  SignInWithOAuthMutation,
  SignInWithOAuthMutationVariables
>;

export const GoogleOneTap = () => {
  const { updateQuery } = useConfigContext();
  const { currentUser } = useCurrentUserContext();
  const { sport } = useSportContext();
  const { formatMessage } = useIntl();
  const params = useOAuthParams({ sport });
  const [consent] = useLocalStorage('trackingConsent', false);
  const { isMobileApp } = useIsMobileApp();
  const [signInWithOAuth] = useMutation(SIGN_IN_WITH_OAUTH_MUTATION);
  const { promptTerms } = useConnectionContext();
  const { showNotification } = useSnackNotificationContext();
  const track = useEvents();
  const { getParams: getUtmParams } = useUtmParams();
  const { pathname } = useLocation();

  const onInvitePage = !!matchAnySportPath(
    catchAll(ANY_SPORT_INVITE),
    pathname
  );
  const disabled = !!currentUser || !consent || isMobileApp || onInvitePage;

  useEffect(() => {
    if (disabled || document.querySelector(`script[src="${SRC}"]`)) {
      return;
    }
    const script = document.createElement('script');
    script.src = SRC;
    document.body.appendChild(script);
  }, [disabled]);

  useEffect(() => {
    if (disabled) return () => {};

    window.handleOneTapCredential = async response => {
      track('Click Google One Tap', {
        sport,
      });
      if (!response?.credential) {
        showNotification('errors', {
          errors: [
            formatMessage({
              defaultMessage:
                'Google authentication failed. Please try again or use another sign-in method.',
            }),
          ],
        });
        return;
      }
      const result = await signInWithOAuth({
        variables: {
          input: {
            idToken: response.credential,
            provider: OAuthProvider.GOOGLE_OAUTH2,
            referrer: params.referrer,
            referrerCode: params.referrer_code,
            impactClickId: params.impact_click_id,
            googleClickId: params.google_click_id,
            facebookClickId: params.facebook_click_id,
            twitterClickId: params.twitter_click_id,
            tikTokClickId: params.tik_tok_click_id,
            snapchatClickId: params.snapchat_click_id,
            redditClickId: params.reddit_click_id,
            platform: params.signup_platform,
            signupSport: params.signup_sport,
            utmParams: getUtmParams(),
          },
          sport,
          notificationCategories,
          importantNotificationCategories,
        },
      });
      const tcuToken = result.data?.signInWithOauth?.tcuToken;
      if (tcuToken) {
        promptTerms({ closable: false, tcuToken });
        return;
      }

      const user = result.data?.signInWithOauth?.currentUser;
      if (user) {
        updateQuery(user);
        /** Redirection happens on RedirectAfterSignin */
      } else if (result.data?.signInWithOauth?.errors) {
        showNotification('errors', {
          errors: formatGqlErrors(result.data.signInWithOauth.errors),
        });
      }
    };

    return () => delete window.handleOneTapCredential;
  }, [
    disabled,
    params.facebook_click_id,
    params.google_click_id,
    params.impact_click_id,
    params.referrer,
    params.referrer_code,
    params.signup_platform,
    params.tik_tok_click_id,
    params.snapchat_click_id,
    params.twitter_click_id,
    params.reddit_click_id,
    params.signup_sport,
    promptTerms,
    showNotification,
    signInWithOAuth,
    updateQuery,
    sport,
    track,
    getUtmParams,
    formatMessage,
  ]);

  if (disabled) return null;

  return (
    <div
      id="g_id_onload"
      data-client_id={GOOGLE_OAUTH_CLIENT_ID}
      data-ux_mode="popup"
      data-callback="handleOneTapCredential"
    />
  );
};
